import { Box, Container, Flex, Image, Text, VStack, Skeleton, HStack, Tooltip, Button, Checkbox } from "@chakra-ui/react";
import { AirdropValue } from "../components/AirdropValue";
import { InfoIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { useChecker } from "../hooks/useChecker";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { useEffect, useState } from "react";
import { ThemeColor, palette } from "../theme/palette";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useKyrosApy } from "../hooks/useKyrosApy";
import { useAirdropClaim } from "../hooks/useAirdropClaim";
import { useErrorToast, useSuccessToast } from "../helpers/toast";
import { useTokenPrice } from "../hooks/useTokenPrice";
import { Link } from "../components";
import { useTimestampChecks } from "../hooks/useTimestampChecks";
import { FutarchyProposals } from "../components/FutarchyProposals/FutarchyProposals";

ChartJS.register(ArcElement, ChartTooltip, Legend);

export const KyrosPage = () => {
    const { checker, isLoading, error, fetchTokens } = useChecker();
    const { price } = useTokenPrice("kyros");
    const { apy } = useKyrosApy();
    const { merkleClaim, isLoading: isClaimLoading, error: claimError, fetchAirdropClaim, onchainClaimRewards, claimed } = useAirdropClaim();
    const wallet = useAnchorWallet();
    const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
    const [showCountdown, setShowCountdown] = useState(true);
    const { errorToast } = useErrorToast();
    const { successToast } = useSuccessToast();
    const [accepted, setAccepted] = useState(false);
    const { isAirdropEnded } = useTimestampChecks();

    useEffect(() => {
        if (wallet) {
            fetchTokens(wallet.publicKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wallet]);

    useEffect(() => {
        if (checker?.claimTime) {
            const updateCountdown = () => {
                const now = Date.now();
                const claimTime = checker.claimTime;
                const timeRemaining = claimTime - now;

                if (timeRemaining <= 0) {
                    setShowCountdown(false);
                    setTimeLeft(null);
                    return;
                }

                const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

                setTimeLeft({ days, hours, minutes, seconds });
            };

            updateCountdown();
            const interval = setInterval(updateCountdown, 1000);

            return () => clearInterval(interval);
        }
    }, [checker?.claimTime]);

    useEffect(() => {
        if (claimError) {
            errorToast(claimError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimError]);

    useEffect(() => {
        if (merkleClaim && wallet) {
            onchainClaimRewards(merkleClaim);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merkleClaim]);

    useEffect(() => {
        if (claimed) {
            successToast(`Your airdrop has been claimed! Welcome to the KYROS community!`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimed]);

    return (
        <>

            <Flex direction="column" minHeight="100vh">
                <Container
                    maxW={{ base: "100%", md: "60%" }}
                    flex="1"
                    py={8}
                    display="flex"
                    justifyContent="center"
                >
                    <VStack spacing={16} align="stretch">
                        <Box
                            p={4}
                            borderRadius="8px"
                            backgroundColor="whiteAlpha.100"
                        >
                            <Flex alignItems="center" gap={8} justifyContent="space-between">
                                <VStack align="flex-start" spacing={4}>
                                    <Text fontSize="md" fontWeight="light" color="white">
                                        <b>KYROS</b> is the token powering the Kyros ecosystem.
                                        <br />
                                        <br />
                                        Its restaked version, <b>kyKYROS</b>, earns yield and allows its holders to participate in the governance.
                                    </Text>
                                </VStack>
                                <Image
                                    src="/kyros.svg"
                                    boxSize="100px"
                                    display={{ base: "none", md: "block" }}
                                />
                            </Flex>
                        </Box>

                        {checker && checker.rank && !isAirdropEnded() && (
                            <Box
                                p={4}
                                borderRadius="8px"
                                backgroundColor="whiteAlpha.100"
                            >
                                <Flex alignItems="center" gap={8} justifyContent="space-between">
                                    <Image
                                        src={`/kyros/${checker.rank.image}`}
                                        boxSize="75px"
                                        display={{ base: "none", md: "block" }}
                                    />
                                    <VStack align="flex-start" spacing={4}>
                                        <Text fontSize="md" fontWeight="light" color="white">
                                            {checker.thanks}
                                        </Text>
                                        <Text fontSize="md" fontWeight="light" color="white">
                                            Your Warchest was a success, finishing <b>#{checker.userRank}</b> with more than <b>{Number(checker.userPoints).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b> coins and earning some badges along the way:
                                        </Text>
                                        {(() => {
                                            const BADGES_PER_ROW = 3;
                                            const badgeRows = [];
                                            for (let i = 0; i < checker.badges.length; i += BADGES_PER_ROW) {
                                                badgeRows.push(checker.badges.slice(i, i + BADGES_PER_ROW));
                                            }
                                            return badgeRows.map((row, idx) => (
                                                <HStack mt={idx === 0 ? 4 : 2} key={idx}>
                                                    {row.map((badge, badgeIdx) => (
                                                        <HStack
                                                            key={badge + badgeIdx}
                                                            bg="rgba(95, 244, 244, 0.08)"
                                                            border="0.5px solid"
                                                            borderColor="#5EF4F3"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            width="fit-content"
                                                        >
                                                            <Image src="/logoMark.png" alt="Kyros" width={4} height={4} />
                                                            <Text fontSize="sm" color="#5EF4F3" fontWeight="semibold">{badge}</Text>
                                                        </HStack>
                                                    ))}
                                                </HStack>
                                            ));
                                        })()}
                                    </VStack>
                                </Flex>
                            </Box>
                        )}

                        {/* Countdown Section */}
                        {showCountdown && checker?.claimTime && timeLeft && (
                            <Box
                                p={6}
                                borderRadius="8px"
                                backgroundColor="whiteAlpha.100"
                            >
                                <VStack spacing={6} align="stretch">
                                    <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center">
                                        Airdrop Opens In
                                    </Text>

                                    <Flex justify="center" gap={4} wrap="wrap">
                                        {timeLeft.days > 0 && (
                                            <VStack spacing={2}>
                                                <Box
                                                    bg="rgba(95, 244, 244, 0.10)"
                                                    borderRadius="8px"
                                                    px={3}
                                                    py={4}
                                                    border="0.5px solid"
                                                    borderColor={ThemeColor.KAIROS}
                                                    minW="60px"
                                                    minH="80px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <Text fontSize="3xl" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                        {timeLeft.days}
                                                    </Text>
                                                </Box>
                                                <Text fontSize="sm" color="white" opacity={0.8}>
                                                    Days
                                                </Text>
                                            </VStack>
                                        )}
                                        <VStack spacing={2}>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.10)"
                                                borderRadius="8px"
                                                px={3}
                                                py={4}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="60px"
                                                minH="80px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="3xl" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.hours.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="white" opacity={0.8}>
                                                Hours
                                            </Text>
                                        </VStack>
                                        <VStack spacing={2}>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.10)"
                                                borderRadius="8px"
                                                px={3}
                                                py={4}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="60px"
                                                minH="80px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="3xl" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.minutes.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="white" opacity={0.8}>
                                                Minutes
                                            </Text>
                                        </VStack>
                                        <VStack spacing={2}>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.10)"
                                                borderRadius="8px"
                                                px={3}
                                                py={4}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="60px"
                                                minH="80px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="3xl" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.seconds.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="white" opacity={0.8}>
                                                Seconds
                                            </Text>
                                        </VStack>
                                    </Flex>

                                    <Text fontSize="md" color="white" textAlign="center" opacity={0.8}>
                                        Get ready to claim your KYROS airdrop!
                                    </Text>
                                </VStack>
                            </Box>
                        )}

                        {/* Token Allocation Section */}
                        {!isAirdropEnded() && (
                        <Box
                            p={6}
                            borderRadius="8px"
                            backgroundColor="whiteAlpha.100"
                        >
                            <VStack spacing={6} align="stretch">
                                <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center">
                                    Your Airdrop
                                </Text>

                                {isLoading ? (
                                    <Skeleton height="100px" borderRadius="8px" />
                                ) : (error || (checker && checker.amount === 0)) ? (
                                    <VStack spacing={6} align="center">
                                        <Text textAlign="center">You're not eligible to claim KYROS tokens.</Text>
                                        {checker?.claimTime && checker.claimTime <= Date.now() && (
                                            <Button
                                                className="mint-box-button"
                                                onClick={() => window.open('https://jup.ag/swap?sell=So11111111111111111111111111111111111111112&buy=kyrosJC2dtm6EoLV5wffZsS4RZVm2hRafKZCLsc38JE', '_blank')}
                                                width="100%"
                                            >
                                                Buy KYROS
                                            </Button>
                                        )}
                                    </VStack>
                                ) : checker ? (
                                    <VStack align="center" width="100%" spacing={6}>
                                        <Flex alignItems="center" gap={2} textAlign="center" width="100%">
                                            <VStack align="center" width="100%">
                                                <HStack spacing={4} alignItems="center">
                                                    <Text color={ThemeColor.KAIROS} fontSize="6xl" fontWeight="bold">
                                                        {(checker.amount / 10 ** 9).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                    </Text>
                                                    <Image
                                                        src="/kykyros.svg"
                                                        boxSize="48px"
                                                    />
                                                </HStack>
                                                <HStack alignItems="center" gap={2} mb={6}>
                                                    <Text color="white">
                                                        kyKYROS
                                                    </Text>
                                                    <Tooltip label="kyKYROS is the restaked version of KYROS. It earns yield from protocol revenues and allows its holders to participate in the governance.">
                                                        <InfoOutlineIcon />
                                                    </Tooltip>
                                                </HStack>
                                                {apy && (
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        bg="rgba(95, 244, 244, 0.10)"
                                                        borderRadius="full"
                                                        px={4}
                                                        py={2}
                                                        border="0.5px solid"
                                                        borderColor={ThemeColor.KAIROS}
                                                        boxShadow="0 2px 8px rgba(95,244,244,0.10)"
                                                        justifyContent="center"
                                                        width="fit-content"
                                                        mb={6}
                                                    >
                                                        <Text
                                                            fontWeight="bold"
                                                            color={ThemeColor.KAIROS}
                                                            fontSize="sm"
                                                            letterSpacing="wide"
                                                            lineHeight="1"
                                                        >
                                                            APY: ~ {((apy?.kyros ?? 0) * 100).toFixed(2)}%
                                                        </Text>
                                                    </Box>
                                                )}
                                                {claimed === null && checker.claimTime <= Date.now() && !checker.claimed && (
                                                    <>
                                                        <HStack>
                                                            <Checkbox isChecked={accepted} onChange={({ target }) => setAccepted(target.checked)}>
                                                                I confirm that I have read, understand and accept the{" "}
                                                                <Link to={"/airdrop-terms.pdf"} textColor={"#5FF4F4"} target={"_blank"}>
                                                                    Airdrop Terms of Use
                                                                </Link>
                                                            </Checkbox>
                                                        </HStack>
                                                        <Button
                                                            className="mint-box-button"
                                                            onClick={() => {
                                                                if (wallet) {
                                                                    fetchAirdropClaim(wallet.publicKey);
                                                                }
                                                            }}
                                                            isLoading={isClaimLoading}
                                                            isDisabled={!accepted}
                                                            width="100%"
                                                            mt={4}
                                                        >
                                                            Claim Airdrop
                                                        </Button>
                                                    </>
                                                )}
                                                {(claimed || checker.claimed) && (
                                                    <Button
                                                        className="mint-box-button"
                                                        width="100%"
                                                        disabled
                                                    >
                                                        Claimed
                                                    </Button>
                                                )}
                                            </VStack>
                                        </Flex>
                                    </VStack>
                                ) : (
                                    <Box p={4} borderRadius="8px" backgroundColor="whiteAlpha.200" color="white">
                                        <Text textAlign="center">Connect your wallet to view your token allocation</Text>
                                    </Box>
                                )}
                            </VStack>
                        </Box>
                        )}



                        {/* Airdrop Value Section */}
                        {checker && checker.amount > 0 && !isAirdropEnded() && (
                            <AirdropValue
                                checker={checker}
                                minFdv={1}
                                maxFdv={250}
                                numPoints={50}
                                totalSupply={50}
                                price={price || 0.1}
                            />
                        )}

                        {/* Futarchy Governance Section */}
                        {isAirdropEnded() && (
                            <FutarchyProposals />
                        )}

                        {/* Tokenomics Section */}
                        <Box
                            p={6}
                            borderRadius="8px"
                            backgroundColor="whiteAlpha.100"
                        >
                            <VStack spacing={6} align="stretch">
                                <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center">
                                    Tokenomics
                                </Text>

                                <HStack justify="center" spacing={2}>
                                    <Text fontSize="md" color="white" textAlign="center">
                                        Total Supply: <b>45.6M KYROS</b>
                                    </Text>
                                    <Tooltip label="The whole supply of KYROS is liquid. The DAO, governed by kyKYROS holders, has the right to mint new tokens in the future.">
                                        <InfoIcon style={{ position: 'relative', top: '-1px' }} />
                                    </Tooltip>
                                </HStack>

                                <Box width="75%" maxW="500px" mx="auto" position="relative">
                                    <Pie
                                        data={{
                                            labels: ['Airdrop', 'Burned', 'Future Community Rewards', 'BorgPad & SwissBorg Alpha', 'Liquidity', 'DAO Reserves', 'Core Contributors'],
                                            datasets: [
                                                {
                                                    data: [14, 9, 25, 11, 12, 12, 15],
                                                    backgroundColor: [
                                                        palette[ThemeColor.KAIROS],
                                                        palette[ThemeColor.KAIROS],
                                                        "#33D1DD",
                                                        "#ACEB73",
                                                        "#02590F",
                                                        "#FF494A",
                                                        "#FFFFFF",
                                                    ],
                                                    borderColor: [
                                                        "#1A1F24",
                                                        "#1A1F24",
                                                        "#1A1F24",
                                                        "#1A1F24",
                                                        "#1A1F24",
                                                    ],
                                                    borderWidth: 2,
                                                    hoverBorderWidth: 4,
                                                    hoverBorderColor: '1A1F24',
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            plugins: {
                                                legend: {
                                                    position: 'bottom',
                                                    labels: {
                                                        color: 'white',
                                                        font: {
                                                            family: 'Poppins, sans-serif',
                                                            size: 14,
                                                            weight: 'normal'
                                                        },
                                                        padding: 25,
                                                        usePointStyle: true,
                                                        pointStyle: 'circle',
                                                    }
                                                },
                                                tooltip: {
                                                    backgroundColor: 'rgba(12, 17, 23, 0.95)',
                                                    titleColor: "white",
                                                    bodyColor: 'white',
                                                    borderColor: 'transparent',
                                                    borderWidth: 0,
                                                    cornerRadius: 8,
                                                    displayColors: false,
                                                    titleFont: {
                                                        family: 'Poppins, sans-serif',
                                                        size: 16,
                                                        weight: 'bold'
                                                    },
                                                    bodyFont: {
                                                        family: 'Poppins, sans-serif',
                                                        size: 12,
                                                        weight: 'normal'
                                                    },
                                                    callbacks: {
                                                        title: (context) => context[0].label,
                                                        label: (context) => `${context.parsed}% of total supply`
                                                    },
                                                    padding: 12
                                                }
                                            },
                                            elements: {
                                                arc: {
                                                    borderRadius: 4,
                                                }
                                            }
                                        }}
                                    />
                                </Box>

                                <VStack spacing={3} align="stretch">
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <VStack>
                                            <Text color="white" fontSize="sm" fontWeight="bold">Airdrop</Text>
                                            <Text color="white" fontSize="sm" fontWeight="light" fontStyle="italic">Burned</Text>
                                        </VStack>
                                        <VStack>
                                            <Text color="white" fontSize="sm" fontWeight="bold">25%</Text>
                                            <Text color="white" fontSize="sm" fontWeight="light" fontStyle="italic">9%</Text>
                                        </VStack>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">Future Community Rewards</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">25%</Text>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">BorgPad & SwissBorg Alpha</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">11%</Text>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">Liquidity</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">12%</Text>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">DAO Reserves</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">12%</Text>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">Core Contributors</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">15%</Text>
                                    </HStack>
                                    <HStack justify="space-between" p={3} borderRadius="8px" backgroundColor="whiteAlpha.50">
                                        <Text color="white" fontSize="sm">Burned</Text>
                                        <Text color="white" fontSize="sm" fontWeight="bold">9%</Text>
                                    </HStack>
                                </VStack>
                            </VStack>
                        </Box>
                    </VStack >
                </Container >
            </Flex >
        </>
    )
};